/* eslint-disable @nrwl/nx/enforce-module-boundaries */
import { Box } from "@material-ui/core";
import { Button } from "@roc/ui";
import { observer } from "mobx-react";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import { InternalValuationReviewStore } from "../stores/internalValuationReviewStore";
import { PROPERTY_COMP_IMAGE_MAX_SIZE, PROPERTY_COMP_IMAGE_TYPES } from "../utils/propertyCompImage";

interface UploadImageButtonProps {
  internalValuationReviewStore: InternalValuationReviewStore;
  testId: string;
  //does the upload, the file was already checked (type and size)
  onUpload: (file: File) => Promise<any>;
  label?: string;
}

//Picks a photo, checks its type and size and hands it to onUpload.
export const UploadImageButton = observer(({ internalValuationReviewStore, testId, onUpload, label = "Upload photo" }: UploadImageButtonProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const isMounted = useRef(true);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  const handleChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";//lets the same file be selected again after an error
    if (!file) {
      return;
    }

    const { notificationStore } = internalValuationReviewStore.globalStore;
    if (!PROPERTY_COMP_IMAGE_TYPES.includes(file.type)) {
      notificationStore.showErrorNotification({ message: "Only JPG, PNG or WEBP photos are allowed." });
      return;
    }
    if (file.size > PROPERTY_COMP_IMAGE_MAX_SIZE) {
      notificationStore.showErrorNotification({ message: "The photo must be 5 MB or less." });
      return;
    }

    setUploading(true);
    try {
      await onUpload(file);
    } finally {
      if (isMounted.current) {
        setUploading(false);
      }
    }
  };

  return (
    <Box display="flex" justifyContent="center" marginTop="4px">
      <input
        ref={inputRef}
        type="file"
        hidden
        accept={PROPERTY_COMP_IMAGE_TYPES.join(",")}
        onChange={handleChange}
        data-testid={`${testId}-input`}
      />
      <Button
        testId={testId}
        variant="outlined"
        color="primary"
        size="small"
        disabled={uploading}
        onClick={() => inputRef.current?.click()}
      >
        {label}
      </Button>
    </Box>
  );
});
